import { useEffect, useState } from 'react';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { QuestionViewUnauthorizedEndpoint } from '@core/types/types.endpoint.question';
import { timeAgo } from '@core/util/util.timeAgo';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@frontend/components/ui/avatar';
import { Button } from '@frontend/components/ui/button';
import { Separator } from '@frontend/components/ui/separator';
import { SearchInput } from '@frontend/Search/Input/SearchInput';
import { Answer } from '@frontend/shared/components/Answer';
import { QB_API_TOKEN } from '@frontend/shared/const/const.QB_API_TOKEN';
import { useApi } from '@frontend/shared/hooks/shared.hook.useApi';

export function QuestionPageUnauthorized() {
  const { id = '', token = '' } = useParams<{ id: string; token: string }>();
  const [value, onChange] = useState('');

  const { data, loading, error } = useApi<QuestionViewUnauthorizedEndpoint>(
    '/question/viewUnauthorized',
    {
      id,
      token,
    },
  );

  const navigate = useNavigate();
  const onSearch = (q: string) => {
    if (q) {
      const url = `/questions?q=${q}`;
      return navigate(url);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(QB_API_TOKEN);
    if (token || error) {
      const url = `/q/${id}`;
      return navigate(url);
    }
  }, [error, id, navigate]);

  return (
    <div className="max-w-3xl mx-auto h-auto px-2 pb-24">
      <div className="py-8 flex items-center">
        <p className="text-[28px] leading-10 font-semibold tracking-tight mr-auto text-[#033426] min-h-10">
          {data?.teamName}
        </p>
      </div>
      <SearchInput
        placeholder="Search for other questions…"
        onSearch={onSearch}
        value={value}
        onChange={onChange}
        searchVal=""
      />
      {!data && loading && (
        <h2 className="text-lg font-semibold mt-12">Loading...</h2>
      )}

      {data && (
        <>
          <div className="border-2 border-[rgba(3,52,38,0.9)] rounded-lg bg-white mt-12">
            <div className="flex items-center transition-all px-5">
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={data.verifiedBy.image}
                  alt={data.verifiedBy.name}
                />
                <AvatarFallback>
                  {data.verifiedBy.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="w-full min-h-[70px] flex items-center">
                <p className="font-medium text-[15px] leading-5 text-[#033426] tracking-tight pr-2 ml-4 py-5">
                  {data.question.title}
                </p>
              </div>
            </div>
            <Separator />
            <div className="py-6 px-8">
              <Answer
                question={data.question}
                editable={false}
                style={{ padding: 0, minHeight: 'auto' }}
              />
            </div>
            <div className="text-[#3A3A3A] bg-[rgba(229,252,235,1)] font-medium flex items-center px-5 py-5 rounded-b-lg">
              <IoIosCheckmarkCircle className="text-[#033426] w-6 h-6" />
              <p className="ml-2 text-[rgba(3,52,38,1)] font-normal text-[13px] leading-4">
                Verified by {data.verifiedBy.name}
                {` — ${timeAgo(data.question.verified_at ?? '')}`}
              </p>
            </div>
          </div>
          <div className="flex justify-center mt-12">
            <Button asChild>
              <Link to="/questions">Explore saved answers</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
